import React from 'react';
import { Smartphone, Download, Target, CheckCircle, Zap, Shield, ArrowRight } from 'lucide-react';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const App: React.FC = () => {
  const [userHeroRef, userHeroVisible] = useScrollAnimation(0.01);
  const [businessHeroRef, businessHeroVisible] = useScrollAnimation(0.01);

  return (
    <div className="min-h-screen bg-[#faf8ed] overflow-hidden">
      <Navbar />

      {/* Rita User App - Section 1 */}
      <section
        ref={userHeroRef}
        className="pt-32 pb-20 px-4 min-h-[90vh] flex items-center relative overflow-hidden"
      >
        {/* Animated Background Blobs */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#0c513f]/5 rounded-full blur-[120px] animate-blob"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-chowdeck-pastelYellow/20 rounded-full blur-[100px] animate-blob animation-delay-2000"></div>
        </div>

        <div className="container-premium relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* User App Text */}
            <div className={`space-y-10 transition-all duration-1000 ${userHeroVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
              }`}>
              <div className="inline-flex items-center space-x-2 bg-white border border-secondary-100 text-[#0c513f] px-6 py-2 rounded-full text-sm font-bold shadow-sm">
                <Zap size={16} fill="currentColor" />
                <span>RITA USER APP</span>
              </div>

              <h1 className="heading-huge text-[#0c513f] leading-[1.0] tracking-tighter-premium">
                Simpler Payments.<br />
                <span className="text-secondary-400">Smarter Wallet.</span>
              </h1>

              <p className="text-2xl text-secondary-600 leading-tight font-medium max-w-xl">
                The ultimate companion for your daily transactions. Discover businesses,
                pay instantly via QR, and manage your finances.
              </p>

              {/* Feature Tags */}
              <div className="flex flex-wrap gap-4">
                {['Instant QR', 'Secure Wallet', 'Business Discovery'].map((tag) => (
                  <div key={tag} className="flex items-center space-x-2 text-[#0c513f] bg-white px-4 py-2 rounded-xl text-sm font-bold border-2 border-[#0c513f]/10 shadow-sm">
                    <CheckCircle size={16} className="text-[#0c513f]" fill="currentColor" />
                    <span>{tag}</span>
                  </div>
                ))}
              </div>

              {/* Download Buttons */}
              <div className="flex flex-wrap gap-6 pt-4">
                <button className="btn-chowdeck flex items-center space-x-4 px-8 py-4 bg-[#0c513f] text-white hover:bg-[#0a4535]">
                  <Download className="group-hover:animate-bounce" size={28} />
                  <div className="text-left">
                    <div className="text-[10px] leading-tight opacity-80 font-bold tracking-wider">GET IT ON</div>
                    <div className="text-lg font-black leading-tight uppercase tracking-tight">Google Play</div>
                  </div>
                </button>
                <button className="btn-chowdeck flex items-center space-x-4 px-8 py-4 bg-white text-[#0c513f] border-2 border-[#0c513f] hover:bg-secondary-50 hover:text-[#0c513f]">
                  <Smartphone className="group-hover:rotate-12 transition-transform" size={28} />
                  <div className="text-left">
                    <div className="text-[10px] leading-tight opacity-80 font-bold tracking-wider">DOWNLOAD ON THE</div>
                    <div className="text-lg font-black leading-tight uppercase tracking-tight">App Store</div>
                  </div>
                </button>
              </div>

              {/* QR Code Container */}
              <div className="p-6 bg-white border-2 border-[#0c513f]/5 rounded-[2rem] max-w-sm flex items-center space-x-6 hover:border-[#0c513f]/20 transition-all group cursor-default shadow-lg">
                <div className="bg-[#faf8ed] p-3 rounded-2xl group-hover:scale-105 transition-transform duration-500 shadow-inner">
                  <img src="/rita_qr.png" alt="QR Code" className="w-16 h-16 mix-blend-multiply" />
                </div>
                <div>
                  <h4 className="text-[#0c513f] font-black text-xl mb-1">Scan to Download</h4>
                  <p className="text-secondary-500 text-sm font-bold">Available for Android & iOS</p>
                </div>
              </div>
            </div>

            {/* User App Mockup */}
            <div className={`relative flex justify-center lg:justify-end transition-all duration-1000 delay-300 ${userHeroVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-20 opacity-0 scale-90'
              }`}>
              <div className="relative z-10 w-full max-w-[400px]">
                <div className="absolute -inset-20 bg-[#0c513f]/10 blur-[80px] rounded-full animate-pulse" />
                <img
                  src="/user_remove_bg.png"
                  alt="Rita User App Dashboard"
                  className="w-full drop-shadow-[0_40px_80px_rgba(0,0,0,0.2)] animate-float"
                />
              </div>

              {/* Floating Element - Security */}
              <div className="absolute top-32 -left-10 bg-white p-5 rounded-[1.5rem] shadow-[0_20px_50px_-10px_rgba(0,0,0,0.1)] animate-float-delayed hidden md:block hover:scale-105 transition-all z-20">
                <div className="flex items-center space-x-4">
                  <div className="bg-[#0c513f] p-3 rounded-xl text-white shadow-lg">
                    <Shield size={24} />
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500 font-bold uppercase tracking-wider mb-0.5">Security</p>
                    <p className="text-[#0c513f] font-black text-lg">Bank Grade</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rita Business App - Section 2 */}
      <section
        ref={businessHeroRef}
        className="py-20 md:py-32 px-4 min-h-[90vh] flex items-center relative overflow-hidden bg-[#0c513f] rounded-t-[4rem] -mb-20 z-10"
      >
        <div className="absolute inset-0 bg-grid-premium opacity-10"></div>
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#faf8ed]/5 rounded-full blur-[150px] pointer-events-none"></div>

        <div className="container-premium relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-24 items-center">

            {/* Business App Mockup - Left Side */}
            <div className={`order-2 lg:order-1 relative flex justify-center lg:justify-start transition-all duration-1000 delay-300 ${businessHeroVisible ? 'translate-x-0 opacity-100 scale-100' : '-translate-x-20 opacity-0 scale-90'
              }`}>
              <div className="relative z-10 w-full max-w-[400px]">
                <div className="absolute -inset-20 bg-[#faf8ed]/10 blur-[80px] rounded-full" />
                <img
                  src="/business_Image_bg.png"
                  alt="Rita Business App Dashboard"
                  className="w-full drop-shadow-[0_40px_80px_rgba(0,0,0,0.4)] animate-float-delayed"
                />
              </div>

              {/* Floating Element - Daily Growth */}
              <div className="absolute bottom-32 -right-10 bg-[#faf8ed] p-5 rounded-[1.5rem] shadow-[0_20px_50px_-10px_rgba(0,0,0,0.3)] animate-float hidden md:block hover:scale-105 transition-all z-20">
                <div className="flex items-center space-x-4">
                  <div className="bg-[#0c513f] p-3 rounded-xl text-white">
                    <ArrowRight size={24} className="-rotate-45" />
                  </div>
                  <div>
                    <p className="text-xs text-secondary-500 font-bold uppercase tracking-wider mb-0.5">Revenue</p>
                    <p className="text-[#0c513f] font-black text-xl">+24.8%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business App Text - Right Side */}
            <div className={`order-1 lg:order-2 space-y-10 transition-all duration-1000 ${businessHeroVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
              }`}>
              <div className="inline-flex items-center space-x-2 bg-[#faf8ed]/10 text-[#faf8ed] border border-[#faf8ed]/20 px-6 py-2 rounded-full text-sm font-bold shadow-sm backdrop-blur-sm">
                <Target size={16} />
                <span>RITA BUSINESS APP</span>
              </div>

              <h2 className="heading-huge text-[#faf8ed] leading-[1.0] tracking-tighter-premium">
                Grow Your<br />
                <span className="text-[#faf8ed]/50">Enterprise.</span>
              </h2>

              <p className="text-2xl text-[#faf8ed]/80 leading-tight font-medium max-w-xl">
                The smart choice for modern merchants. Manage sales, track customers,
                and receive payments instantly.
              </p>

              {/* Business Stats */}
              <div className="grid grid-cols-2 gap-8 pt-4">
                <div className="space-y-2">
                  <p className="text-5xl font-black text-[#faf8ed] tracking-tight">0%</p>
                  <p className="text-lg font-bold text-[#faf8ed]/60 uppercase tracking-widest text-xs">Setup Fee</p>
                </div>
                <div className="space-y-2">
                  <p className="text-5xl font-black text-[#faf8ed] tracking-tight">24h</p>
                  <p className="text-lg font-bold text-[#faf8ed]/60 uppercase tracking-widest text-xs">Settlement</p>
                </div>
              </div>

              {/* Download Buttons */}
              <div className="flex flex-wrap gap-6 pt-4">
                <button className="btn-chowdeck flex items-center space-x-4 px-8 py-4 bg-[#faf8ed] text-[#0c513f] hover:bg-white hover:text-[#0c513f] border-2 border-transparent">
                  <Download className="group-hover:animate-bounce" size={28} />
                  <div className="text-left">
                    <div className="text-[10px] leading-tight opacity-70 font-bold tracking-wider">GET IT ON</div>
                    <div className="text-lg font-black leading-tight uppercase tracking-tight">Google Play</div>
                  </div>
                </button>
                <button className="btn-chowdeck flex items-center space-x-4 px-8 py-4 bg-transparent text-[#faf8ed] border-2 border-[#faf8ed]/30 hover:bg-[#faf8ed]/10 hover:border-[#faf8ed]">
                  <Smartphone className="group-hover:rotate-12 transition-transform" size={28} />
                  <div className="text-left">
                    <div className="text-[10px] leading-tight opacity-70 font-bold tracking-wider">DOWNLOAD ON THE</div>
                    <div className="text-lg font-black leading-tight uppercase tracking-tight">App Store</div>
                  </div>
                </button>
              </div>

              {/* QR Code Container */}
              <div className="p-6 bg-[#faf8ed]/5 border-2 border-[#faf8ed]/10 rounded-[2rem] max-w-sm flex items-center space-x-6 hover:border-[#faf8ed]/30 transition-all group cursor-default backdrop-blur-sm">
                <div className="bg-white p-3 rounded-2xl group-hover:scale-105 transition-transform duration-500 shadow-lg">
                  <img src="/rita_qr.png" alt="QR Code" className="w-16 h-16" />
                </div>
                <div>
                  <h4 className="text-[#faf8ed] font-black text-xl mb-1">Merchant Scan</h4>
                  <p className="text-[#faf8ed]/60 text-sm font-bold">Join 5000+ businesses</p>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default App;
